/*
 * Big Twin Store — Les plus vendus
 * Tabs, bike-aware best sellers (Section Rendering API + storefront filters), carousel arrows.
 * Loaded as a deferred ES module by sections/bts-best-sellers.liquid. No dependencies.
 *
 * Bike contract (shared with the bike selector):
 *   localStorage[storageKey] = { list: [vehicle, …], active: index }  or a single vehicle object
 *   vehicle = { fam, model, year, cc?, label?, fit? }   fit = key or [keys] matching the fitment metafield
 *   After saving, dispatch: document.dispatchEvent(new CustomEvent('bts:vehicle-change'))
 */

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
const CHECK =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20 6L9 17l-5-5"/></svg>';

const slug = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

class BtsBestSellers extends HTMLElement {
  connectedCallback() {
    const cfgEl = this.querySelector('script[data-bts-config]');
    try {
      this.cfg = JSON.parse(cfgEl?.textContent || '{}');
    } catch {
      this.cfg = {};
    }
    this.track = this.querySelector('[data-bts-track]');
    if (!this.track) return;

    this.heading = this.querySelector('[data-bts-heading]');
    this.tabs = [...this.querySelectorAll('[data-bts-tab]')];
    this.navs = [...this.querySelectorAll('[data-bts-nav]')];
    this.prevBtns = [...this.querySelectorAll('[data-bts-prev]')];
    this.nextBtns = [...this.querySelectorAll('[data-bts-next]')];
    this.bar = this.querySelector('[data-bts-bar]');
    this.cache = new Map();
    this.req = 0;
    this.ac = new AbortController();

    const on = (el, type, fn, opts = {}) => el.addEventListener(type, fn, { signal: this.ac.signal, ...opts });

    this.tabs.forEach((tab) => {
      on(tab, 'click', (e) => {
        e.preventDefault();
        this.select(tab);
      });
      const warm = () => this.prefetch(tab);
      on(tab, 'pointerenter', warm, { once: true });
      on(tab, 'focus', warm, { once: true });
    });
    if (this.tabs.length) on(this.tabs[0].parentElement, 'keydown', (e) => this.onTabKey(e));

    this.prevBtns.forEach((b) => on(b, 'click', () => this.go(-1)));
    this.nextBtns.forEach((b) => on(b, 'click', () => this.go(1)));
    on(this.track, 'scroll', () => this.queueNav(), { passive: true });
    this.ro = new ResizeObserver(() => this.queueNav());
    this.ro.observe(this.track);

    on(document, 'bts:vehicle-change', () => this.onVehicle());
    on(window, 'storage', (e) => {
      if (e.key === this.cfg.storageKey) this.onVehicle();
    });
    on(document, 'shopify:block:select', (e) => {
      const tab = this.tabs.find((t) => t === e.target || e.target.contains?.(t));
      if (tab) this.select(tab);
    });

    // The first tab is server-rendered: seed the cache so coming back to it is instant.
    const active = this.activeTab();
    this.cache.set(this.url(active, false), Promise.resolve({ html: this.track.innerHTML, filtered: false }));

    this.vehicle = this.readVehicle();
    if (this.isPersonal()) this.load(active);
    else this.applyFit();
    this.queueNav();
  }

  disconnectedCallback() {
    this.ac?.abort();
    this.ro?.disconnect();
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  /* ---------- Bike ---------- */

  readVehicle() {
    try {
      const raw = JSON.parse(localStorage.getItem(this.cfg.storageKey) || 'null');
      const v = raw && Array.isArray(raw.list) ? raw.list[raw.active] : raw;
      return v && typeof v === 'object' ? v : null;
    } catch {
      return null;
    }
  }

  fitKeys(v = this.vehicle) {
    if (!v) return [];
    if (Array.isArray(v.fit)) return v.fit.map(String).filter(Boolean);
    if (typeof v.fit === 'string' && v.fit) return v.fit.split('|').filter(Boolean);
    const key = (this.cfg.pattern || '')
      .replace(/\{(\w+)\}/g, (_, name) => slug(v[name]))
      .replace(/-{2,}/g, '-')
      .replace(/^-|-$/g, '');
    return key ? [key] : [];
  }

  vehicleLabel(v = this.vehicle) {
    return v ? v.label || [v.model, v.year].filter(Boolean).join(' ') : '';
  }

  isPersonal() {
    return Boolean(this.cfg.personalize && this.vehicle && this.fitKeys().length);
  }

  onVehicle() {
    this.vehicle = this.readVehicle();
    this.load(this.activeTab());
  }

  /* ---------- Data ---------- */

  activeTab() {
    return this.tabs.find((t) => t.getAttribute('aria-selected') === 'true') || null;
  }

  url(tab, personal = this.isPersonal()) {
    const base = tab ? tab.getAttribute('href') : this.track.dataset.url;
    const u = new URL(base, location.origin);
    if (personal) {
      this.fitKeys().forEach((k) => u.searchParams.append(this.cfg.param, k));
      if (this.cfg.universal) u.searchParams.append(this.cfg.param, this.cfg.universal);
    }
    u.searchParams.set('section_id', this.cfg.file);
    return u.pathname + u.search;
  }

  fetchList(url) {
    if (!this.cache.has(url)) {
      const request = fetch(url, { credentials: 'same-origin' })
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.text();
        })
        .then((text) => {
          const list = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-bts-fragment]');
          if (!list) throw new Error('Fragment not found: check the section file name.');
          [...list.children].slice(this.cfg.limit || 4).forEach((n) => n.remove());
          return { html: list.innerHTML.trim(), filtered: list.dataset.filtered === 'true' };
        });
      request.catch(() => this.cache.delete(url));
      this.cache.set(url, request);
    }
    return this.cache.get(url);
  }

  prefetch(tab) {
    this.fetchList(this.url(tab)).catch(() => {});
  }

  async load(tab) {
    const id = ++this.req;
    const personal = this.isPersonal();
    this.track.setAttribute('aria-busy', 'true');

    let result;
    try {
      result = await this.fetchList(this.url(tab, personal));
    } catch (err) {
      if (id === this.req) this.track.removeAttribute('aria-busy');
      console.warn('[bts-best-sellers]', err);
      return;
    }
    if (id !== this.req) return;

    const applied = personal && result.filtered;
    if (personal && !result.filtered) {
      console.warn('[bts-best-sellers] Bike filter not applied: enable the fitment metafield in Search & Discovery > Filters.');
    }

    if (result.html) {
      this.track.innerHTML = result.html;
    } else {
      const li = document.createElement('li');
      li.className = 'bts-bs__empty';
      li.textContent = (this.cfg.empty || '').replace('{vehicle}', this.vehicleLabel());
      this.track.replaceChildren(li);
    }

    this.track.scrollLeft = 0;
    this.setHeading(applied);
    this.applyFit();
    this.track.removeAttribute('aria-busy');
    document.dispatchEvent(new CustomEvent('currency:update'));
    this.queueNav();
  }

  setHeading(personal) {
    if (!this.heading) return;
    const text = personal && this.cfg.headingBike
      ? this.cfg.headingBike.replace('{vehicle}', this.vehicleLabel())
      : this.cfg.heading;
    if (text && this.heading.textContent !== text) this.heading.textContent = text;
  }

  applyFit() {
    if (!this.cfg.fitBadges || !this.vehicle) return;
    const bikeKeys = this.fitKeys();
    if (!bikeKeys.length) return;
    const label = this.vehicleLabel();
    const model = this.vehicle.model || label;

    this.track.querySelectorAll('[data-fit]').forEach((card) => {
      const badge = card.querySelector('[data-fit-badge]');
      const keys = (card.dataset.fit || '').split('|').filter(Boolean);
      if (!badge || !keys.length) return;
      if (this.cfg.universal && keys.includes(this.cfg.universal)) return;
      const fits = keys.some((k) => bikeKeys.includes(k));
      badge.hidden = false;
      badge.className = `bts-fit bts-fit--${fits ? 'yes' : 'no'}`;
      badge.textContent = fits ? `${this.cfg.tYes} ${label}` : `${this.cfg.tNo} ${model}`;
      if (fits) badge.insertAdjacentHTML('afterbegin', CHECK);
    });
  }

  /* ---------- Tabs ---------- */

  select(tab, focus = false) {
    if (focus) tab.focus();
    if (tab.getAttribute('aria-selected') === 'true') return;
    this.tabs.forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
    });
    this.querySelector('[role="tabpanel"]')?.setAttribute('aria-labelledby', tab.id);
    const strip = tab.parentElement;
    if (strip.scrollWidth > strip.clientWidth) {
      const target = tab.offsetLeft - (strip.clientWidth - tab.offsetWidth) / 2;
      strip.scrollTo({ left: target, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }
    this.load(tab);
  }

  onTabKey(e) {
    const i = this.tabs.indexOf(document.activeElement);
    if (i < 0) return;
    const last = this.tabs.length - 1;
    const rtl = getComputedStyle(this).direction === 'rtl';
    const next = rtl ? 'ArrowLeft' : 'ArrowRight';
    const prev = rtl ? 'ArrowRight' : 'ArrowLeft';
    let n = null;
    if (e.key === next) n = i === last ? 0 : i + 1;
    else if (e.key === prev) n = i === 0 ? last : i - 1;
    else if (e.key === 'Home') n = 0;
    else if (e.key === 'End') n = last;
    else if (e.key === ' ') n = i;
    if (n === null) return;
    e.preventDefault();
    this.select(this.tabs[n], true);
  }

  /* ---------- Carousel ---------- */

  go(dir) {
    const t = this.track;
    const item = t.querySelector('.bts-bs__item');
    const style = getComputedStyle(t);
    const gap = parseFloat(style.columnGap) || 0;
    const step = item ? item.getBoundingClientRect().width + gap : t.clientWidth;
    const perView = Math.max(1, Math.floor((t.clientWidth + gap) / step));
    const sign = style.direction === 'rtl' ? -1 : 1;
    t.scrollBy({ left: dir * sign * step * perView, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  queueNav() {
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = 0;
      this.updateNav();
    });
  }

  updateNav() {
    const t = this.track;
    const max = t.scrollWidth - t.clientWidth;
    const pos = Math.abs(t.scrollLeft);
    const scrollable = max > 2;
    this.navs.forEach((n) => {
      n.hidden = !scrollable;
    });
    if (!scrollable) return;
    this.prevBtns.forEach((b) => {
      b.disabled = pos <= 2;
    });
    this.nextBtns.forEach((b) => {
      b.disabled = pos >= max - 2;
    });
    if (this.bar) {
      this.bar.style.setProperty('--bs-thumb', (t.clientWidth / t.scrollWidth).toFixed(4));
      this.bar.style.setProperty('--bs-prog', Math.min(1, pos / max).toFixed(4));
    }
  }
}

if (!customElements.get('bts-best-sellers')) customElements.define('bts-best-sellers', BtsBestSellers);